<template>
  <BaseCard>
    <div class="flex flex-col text-gray lg:px-3 py-8 gap-14">
      <div class="flex flex-col items-center gap-y-5">
        <div class="flex items-center gap-2">
          <div
            class="w-[40px] h-[40px] rounded-full border-3 border-primary flex justify-center items-center"
          >
            <BaseIcon
              name="outline_check"
              size="none"
              class="w-12 text-primary"
            />
          </div>
          <h2 class="font-bold text-2xl">
            {{ $t('showcases.success_title') }}
          </h2>
        </div>
        <div v-if="game" class="flex flex-col items-center text-center">
          <BaseLocaleLink :to="gameLink" class="xl:row-span-2 hover:opacity-70">
            <div class="w-40">
              <BaseRatio
                :ratio="1"
                size="cover"
                :src="game.icon?.url || ''"
                class="rounded-xl bg-primary-50"
              >
                <div>
                  <BaseIcon
                    v-if="!game.icon?.url"
                    name="outline_gamepad"
                    size="none"
                    class="text-primary h-14 w-14"
                  />
                </div>
              </BaseRatio>
            </div>
          </BaseLocaleLink>
          <div>
            <BaseLocaleLink
              :to="gameLink"
              class="font-bold text-lg hover:text-gray-700"
            >
              {{ game.name }}
            </BaseLocaleLink>
            <div v-if="game.company?.name" class="text-gray-500">
              {{ game.company.name }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isLoaded" class="py-10 flex justify-center">
        <BaseSpinner />
      </div>
      <template v-else>
        <template v-if="!otherShowcases?.length && !otherGames?.length">
          <BaseLocaleLink v-if="gameLink" :to="gameLink" class="mt-6">
            <BaseButton size="lg">
              {{ $t('showcases.go_to_game_page') }}
            </BaseButton>
          </BaseLocaleLink>
          <BaseButton
            look="link"
            class="mt-4 w-full"
            @click="onSubmitAnotherGame(null)"
          >
            {{ $t('showcases.submit_another_game') }}
          </BaseButton>
        </template>
        <div
          v-else
          class="grid grid-cols-1"
          :class="
            otherShowcases?.length &&
            '2xl:grid-cols-[1fr_min-content] 2xl:grid-rows-[max-content_max-content] 2xl:grid-flow-col 2xl:gap-x-12'
          "
        >
          <template v-if="otherShowcases?.length">
            <div
              class="relative ml-11 font-bold text-lg leading-none flex items-center"
            >
              <BaseIcon
                name="outline_arrow_circle_right"
                size="lg"
                class="absolute -left-11 text-primary"
              />
              <div class="relative w-0 flex-1">
                {{ $t('showcases.success_more_showcases') }}
              </div>
            </div>
            <div class="ml-11 flex flex-col pt-2">
              <div
                class="border-b border-ocean pb-1 mb-1 text-xs text-gray-400 flex justify-between"
              >
                <div>
                  {{ $t('events.event') }}
                </div>
                <div>
                  {{ $t('showcases.deadline') }}
                </div>
              </div>
              <div class="flex flex-col gap-y-5">
                <div
                  v-for="otherShowcase in otherShowcases"
                  :key="otherShowcase.id"
                  class="flex justify-between gap-x-5"
                >
                  <div class="flex flex-col">
                    <BaseLocaleLink
                      class="text-primary hover:text-primary-400 font-bold break-words"
                      :to="
                        getShowcasesFormLink(
                          otherShowcase.event.slug,
                          otherShowcase.slug
                        )
                      "
                      >{{ otherShowcase.event.name }}
                    </BaseLocaleLink>
                    <div class="text-gray-400">
                      {{ getEventDate(otherShowcase.event) }}
                    </div>
                  </div>
                  <div class="shrink-0 max-w-[45%]">
                    {{ getUntilDate(otherShowcase) }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div
            class="relative ml-11 font-bold text-lg leading-none flex items-center"
            :class="otherShowcases?.length && 'mt-10 2xl:mt-0'"
          >
            <BaseIcon
              name="outline_arrow_circle_right"
              size="lg"
              class="absolute -left-11 text-primary"
            />
            <div class="relative w-0 flex-1">
              {{ $t('showcases.success_more_games') }}
              <div
                v-if="companyGamesList.loaded && !allCompanyGamesLoaded"
                class="text-gray-400 text-sm pt-1 font-normal 2xl:-mb-6"
              >
                {{ $t('showcases.most_recent_games_displayed') }}
              </div>
            </div>
          </div>
          <div
            class="ml-11 flex flex-wrap gap-5 place-content-start pt-5"
            :class="
              otherShowcases?.length &&
              '2xl:grid 2xl:grid-cols-[min-content_min-content] 2xl:pr-5 2xl:pt-7'
            "
          >
            <div
              v-for="otherGame in otherGames"
              :key="otherGame.id"
              class="group w-[108px] shrink-0 cursor-pointer flex flex-col gap-y-2 leading-tight hover:text-gray-600"
              @click="onSubmitAnotherGame(otherGame)"
            >
              <BaseRatio
                :ratio="3 / 4"
                :src="otherGame.icon?.url"
                class="relative flex justify-center items-center rounded-xl"
                :class="!otherGame.icon?.url && 'bg-primary-100'"
              >
                <div
                  class="absolute inset-0 rounded-xl border-primary border-2 opacity-0 group-hover:opacity-100"
                />
                <BaseIcon
                  v-if="!otherGame.icon?.url"
                  name="outline_gamepad"
                  size="xl"
                  class="text-primary"
                />
              </BaseRatio>
              <div class="break-words">
                {{ otherGame.name }}
              </div>
            </div>
            <div
              class="group w-[108px] shrink-0 cursor-pointer flex flex-col gap-y-2 leading-tight hover:text-gray-600"
              @click="onSubmitAnotherGame(null)"
            >
              <BaseRatio
                :ratio="3 / 4"
                class="relative flex justify-center items-center rounded-xl bg-primary-100"
              >
                <div
                  class="absolute inset-0 rounded-xl border-primary border-2 opacity-0 group-hover:opacity-100"
                />
                <BaseIcon name="outline_plus" size="xl" class="text-primary" />
              </BaseRatio>
              <div class="break-words">
                {{ $t('showcases.new_game') }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import { useGames } from '~/stores/games'
import type { GameLite } from '~/models/games'
import { useShowcases } from '~/stores/showcases'
import type { ShowcaseLite } from '~/models/showcases'
import type { EventLite } from '~/models/events'
import { useAuth } from '~/stores/auth'

const gamesStore = useGames()
const showcasesStore = useShowcases()
const authStore = useAuth()
const { getHumanizedDatePeriod } = useDate()

const emits = defineEmits<{
  (e: 'submitAnotherGame', value: GameLite | null): void
}>()

const { game, companyGamesList, allCompanyGamesLoaded } =
  storeToRefs(gamesStore)
const { fetchCompanyGames, resetCompanyGames } = gamesStore
const { showcase, availableShowcases } = storeToRefs(showcasesStore)
const {
  fetchAvailableShowcases,
  resetAvailableShowcases,
  getShowcasesFormLink,
  getUntilDate,
} = showcasesStore

const isLoaded = ref<boolean>(false)

onMounted(async () => {
  await fetchOther()
})

async function fetchOther(): Promise<void> {
  isLoaded.value = false

  resetAvailableShowcases()
  resetCompanyGames()

  const companyId = authStore.profile?.company_id

  await Promise.all([
    fetchAvailableShowcases(),
    companyId && fetchCompanyGames(companyId),
  ])

  isLoaded.value = true
}

const otherShowcases = computed<ShowcaseLite[]>(
  () =>
    availableShowcases.value?.filter(
      (availableShowcase) => availableShowcase.id !== showcase.value?.id
    ) || []
)

function getEventDate(event?: EventLite): string {
  if (!event) {
    return ''
  }

  return (
    getEventCustomDate(event) ||
    getHumanizedDatePeriod(event.date_from, event.date_to, event.timezone, 'en')
  )
}

function getEventCustomDate(event: EventLite): string {
  if (!event.custom_date) {
    return ''
  }

  const parsedDate = parseFieldLocales(event.custom_date)
  return getFieldLocale(parsedDate || '', 'en')
}

const otherGames = computed<GameLite[]>(() =>
  [...(companyGamesList.value.hits || [])]
    .filter((companyGame) => companyGame.id !== game.value?.id)
    .reverse()
)

const gameLink = computed(() => {
  return game.value?.id ? gamesStore.getProfileGamePageLink(game.value.id) : ''
})

function onSubmitAnotherGame(game: GameLite | null): void {
  emits('submitAnotherGame', game)
}
</script>
